import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useAddConfig, useRefreshAggregatedRule } from '@app/sdk';
import { Segment } from 'semantic-ui-react';
import { AddConfigEditor } from './AddConfigEditor';
import { AddConfigSteps } from './AddConfigSteps';
import { CategorySelector } from './CategorySelector';
import { editor } from 'monaco-editor';
import { AffectedProducts } from './AffectedProducts';
import { Loading } from '../components/LoadingWrapper';
import { OuSelector } from '../updateConfig/OuSelector';
import { toast } from 'react-toastify';
import { ContentHeader } from '@app/shared';
import { ProductOrMerchant } from '../updateConfig/ProductOrMerchant';
import { AffectedMerchants } from './AffectedMerchants';

export function AddConfig({
  item,
  enableAddConfig,
  type,
}: {
  item: ProductOrMerchant | undefined;
  enableAddConfig: Dispatch<SetStateAction<boolean>>;
  type: 'product' | 'merchant';
}) {
  const [step, setStep] = useState(1);
  const [editor, setEditor] = useState<editor.IStandaloneCodeEditor>();
  const [editorContent, setEditorContent] = useState('');
  const [selectedOuId, setSelectedOuId] = useState<string>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<any>('');

  useEffect(() => {
    if (item) {
      setSelectedOuId(item.organisationUnitId);
    }
  }, [item]);

  const cancel = () => {
    enableAddConfig(false);
  };

  useEffect(() => {
    setSelectedCategoryId('');
    setEditor(undefined);
    setStep(1);
    setEditorContent(undefined);
  }, [selectedOuId]);

  useEffect(() => {
    setEditorContent(undefined);
  }, [selectedCategoryId]);

  const renderSteps = useCallback(
    (step: number) => {
      switch (step) {
        case 1:
          return type === 'product' ? (
            <AffectedProducts selectedOuId={selectedOuId} />
          ) : (
            <AffectedMerchants selectedOuId={selectedOuId} />
          );
        case 2:
          return (
            <AddConfigEditor
              key={selectedOuId + selectedCategoryId}
              selectedOuId={selectedOuId}
              categoryId={selectedCategoryId}
              setEditor={setEditor}
              editor={editor}
              editorContent={editorContent}
            />
          );

        default:
          return <></>;
      }
    },
    [editor, editorContent, selectedCategoryId, selectedOuId, type]
  );

  const { mutateAsync: addConfig, isPending } = useAddConfig();
  const addConfigCallBack = useCallback(async () => {
    const value = editor?.getValue();
    let req = {} as any;
    try {
      req = JSON.parse(value);
    } catch (e) {
      toast.error('Invalid JSON');
      return false;
    }
    return await addConfig({
      request: req,
      categoryId: selectedCategoryId,
      organisationUnitId: selectedOuId,
    });
  }, [editor, addConfig, selectedCategoryId, selectedOuId]);

  const refreshAggregatedRule = useRefreshAggregatedRule();

  const next = async () => {
    switch (step) {
      case 1:
        setStep((step) => step + 1);
        break;
      case 2:
        if (await addConfigCallBack()) {
          setTimeout(() => {
            refreshAggregatedRule();
          }, 50);
          enableAddConfig(false);
        }
        break;
      default:
        break;
    }
  };

  const previous = () => {
    setEditorContent(editor?.getValue());
    setStep((step) => step - 1);
  };

  return (
    <div>
      <ContentHeader>
        Add Config
        <button className={'ui button primary'} onClick={() => cancel()}>
          Cancel
        </button>
      </ContentHeader>

      <div className={'ui form'}>
        <OuSelector item={item} selectedOuId={selectedOuId} setSelectedOuId={setSelectedOuId} />
        <CategorySelector
          key={selectedOuId}
          type={type}
          selectedCategory={selectedCategoryId}
          setSelectedCategory={(categoryId: string) => {
            setSelectedCategoryId(categoryId);
          }}
        />
        {selectedCategoryId && (
          <>
            <AddConfigSteps step={step} type={type} />
            {renderSteps(step)}

            <div className={'fields'} style={{ paddingTop: '5px' }}>
              <button className={'ui button primary'} onClick={() => previous()} disabled={step === 1}>
                Back
              </button>

              <button className={'ui button primary'} onClick={() => next()}>
                {step == 1 ? 'Next' : 'Confirm Add Config'}
              </button>
              <Loading loading={isPending} />
            </div>
          </>
        )}

        {!selectedCategoryId && (
          <Segment style={{ height: '450px' }}>
            <ContentHeader>Select a category to start</ContentHeader>
          </Segment>
        )}
      </div>
    </div>
  );
}
